import React, { useEffect, useState, useMemo } from "react"
import { Modal, Button } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"
import PropTypes from "prop-types"

import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"
import { API_STATUS } from "../../../../shared_components/widgets/utils"
import { addNewObject, getObjectsList } from "../../../../Loaders"
import PageLoadingPanel from "../../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../../shared_components/widgets/PageErrorPanel"
import { logToServer } from "../../../../ErrorBoundary"
import { Required } from "../../../../shared_components/widgets/utils"

const defaultPrintingInfo = {
  methods: [],
  printer_location: "",
  copies: 1,
}

export default function ModalPrintLabels({ sample, show, printers = {}, setPrinters, onHide, onSuccess }) {
  const intl = useIntl()

  const [statusData, setStatusData] = useState(API_STATUS.LOADING)
  const [statusPrint, setStatusPrint] = useState(null)
  const [selectedSampleIDs, setSelectedSampleIDs] = useState([])
  const [samplePrintSettings, setSamplePrintSettings] = useState({})

  // Check if we're dealing with multiple samples or a single sample
  const isMultipleSamples = Array.isArray(sample)

  const headers = [
    { id: "samplemanagement.sample.id", desc: "Sample ID" },
    { id: "samplemanagement.methods", desc: "Methods", isRequired: "*" },
    {
      id: "samplemanagement.printer.location",
      desc: "Printer Location",
      isRequired: "*",
      hide: isMultipleSamples,
    },
    { id: "samplemanagement.print.copies", desc: "Number of Copies", isRequired: "*" },
  ]

  // Filter samples that are pending analysis
  const pendingAnalysisSamples = useMemo(() => {
    return isMultipleSamples ? sample : [sample]
  }, [sample, isMultipleSamples])

  // Get the selected samples based on selectedSampleIDs
  const selectedSamples = useMemo(() => {
    return pendingAnalysisSamples.filter((s) => selectedSampleIDs.includes(s.id))
  }, [pendingAnalysisSamples, selectedSampleIDs])

  useEffect(() => {
    onPageLoad()
  }, [])

  const onPageLoad = () => {
    if (isMultipleSamples) {
      // Initialize print settings for multiple samples
      const initialPrintSettings = pendingAnalysisSamples.reduce(
        (acc, s) => ({
          ...acc,
          [s.id]: {
            sample: s.id,
            ...defaultPrintingInfo,
          },
        }),
        {},
      )
      setSamplePrintSettings(initialPrintSettings)
      setSelectedSampleIDs([])
    } else {
      // Initialize print settings for a single sample
      setSamplePrintSettings({
        [sample.id]: {
          sample: sample.id,
          ...defaultPrintingInfo,
        },
      })
      setSelectedSampleIDs([sample.id])
    }

    // Load printers if not already loaded
    if (Object.keys(printers).length) {
      setStatusData(API_STATUS.SUCCESS)
    } else {
      getObjectsList("/laboratory/sample-management/sample/get_printers/")
        .then((data) => {
          setStatusData(API_STATUS.SUCCESS)
          setPrinters(data)
        })
        .catch((error) => {
          setStatusData(API_STATUS.ERROR)
          console.error(error)
          logToServer(error)
        })
    }
  }

  // Update print settings for a specific sample
  const updatePrintSettings = (sampleId, field, value) => {
    setSamplePrintSettings((prev) => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [field]: value,
      },
    }))
  }

  const [formValid, setFormValid] = useState(false)
  const [sharedPrinter, setSharedPrinter] = useState("")
  const multiSelectPrinters = Object.keys(printers).map((s) => {
    return { value: s }
  })
  useEffect(() => {
    // Validate if all selected samples have required fields filled
    let sampleRowsComplete
    if (!isMultipleSamples) {
      sampleRowsComplete = selectedSampleIDs.every(
        (sampleId) =>
          samplePrintSettings[sampleId].methods.length && samplePrintSettings[sampleId].printer_location,
      )
    } else {
      sampleRowsComplete =
        selectedSampleIDs.every((sampleId) => samplePrintSettings[sampleId].methods.length) &&
        sharedPrinter !== ""
    }
    setFormValid(sampleRowsComplete && selectedSampleIDs.length > 0)
  }, [selectedSampleIDs, samplePrintSettings, sharedPrinter])

  const onSubmitPrintLabels = () => {
    if (!formValid) {
      alert(
        intl.formatMessage({
          id: "general.server.error",
        }),
      )
      return
    }

    // Create print jobs for selected samples
    let printJobs
    if (!isMultipleSamples) {
      printJobs = selectedSampleIDs.map((sampleId) => ({
        ...samplePrintSettings[sampleId],
      }))
    } else {
      printJobs = selectedSampleIDs.map((sampleId) => ({
        ...samplePrintSettings[sampleId],
        printer_location: sharedPrinter,
      }))
    }

    setStatusPrint(API_STATUS.LOADING)

    Promise.all(
      printJobs.map((job) => addNewObject("/laboratory/sample-management/sample/print_sample_labels/", job)),
    )
      .then(() => {
        setStatusPrint(API_STATUS.SUCCESS)
        onSuccess()
      })
      .catch((error) => {
        setStatusPrint(API_STATUS.ERROR)
        logToServer(`Couldn't print labels: ${error}`)
        console.error(error)
        alert(
          intl.formatMessage({
            id: "samplemanagement.print.job.error",
          }),
        )
      })
  }

  // Render a single row in the sample table
  const renderSampleRow = (sampleItem) => (
    <tr key={sampleItem.id}>
      <td className={"text-center"}>
        {sampleItem.sample_id}
        {sampleItem.alias && <span style={{ fontSize: "12px" }}>{`(${sampleItem.alias})`}</span>}
      </td>
      <td className={"text-center"} style={{ width: "200px" }}>
        <ReactSelectionElement
          name={sampleItem.sample_id}
          list={sampleItem.analyzable_methods.map((s) => {
            return { value: s }
          })}
          onValueChange={(e, opts) =>
            updatePrintSettings(
              sampleItem.id,
              "methods",
              opts.map((s) => s.value),
            )
          }
        />
      </td>
      {!isMultipleSamples && (
        <td className={"text-center"}>
          <ReactSelectionElement
            name={`${sample.id}_printer_location`}
            list={multiSelectPrinters}
            value={
              samplePrintSettings[sampleItem.id]?.printer_location
                ? { value: samplePrintSettings[sampleItem.id]?.printer_location }
                : null
            }
            onValueChange={(_, selectedLocation) =>
              updatePrintSettings(sampleItem.id, "printer_location", selectedLocation?.value || "")
            }
            isMulti={false}
          />
        </td>
      )}
      <td className={"text-center"}>
        <input
          type={"number"}
          min={1}
          value={samplePrintSettings[sampleItem.id]?.copies || 1}
          onWheel={(e) => e.target.blur()}
          className={"form-control form-control-sm smp_data_input"}
          onChange={(e) => updatePrintSettings(sampleItem.id, "copies", parseInt(e.target.value))}
        />
      </td>
    </tr>
  )

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id={"samplemanagement.print.labels"} description={"Print Labels Title"} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ width: "700px" }}>
        {statusData === API_STATUS.LOADING && <PageLoadingPanel />}
        {statusData === API_STATUS.ERROR && <PageErrorPanel attemptReload={onPageLoad} />}
        {statusData === API_STATUS.SUCCESS &&
          (Object.keys(printers).length ? (
            <>
              {isMultipleSamples && (
                <div className="row">
                  <div className="col-6">
                    <ReactSelectionElement
                      name={"sample-selector"}
                      label={intl.formatMessage({
                        id: "samplemanagement.print.dropdown.label",
                      })}
                      list={pendingAnalysisSamples}
                      onValueChange={(_, selectedOptions) => {
                        const options = selectedOptions.map((option) => option.id)
                        // Clear settings for samples that were unselected
                        selectedSampleIDs
                          .filter((id) => !options.includes(id))
                          .forEach((id) => {
                            setSamplePrintSettings((prev) => ({
                              ...prev,
                              [id]: {
                                ...prev[id],
                                ...defaultPrintingInfo,
                              },
                            }))
                          })
                        setSelectedSampleIDs(options)
                      }}
                      getOptionValue={(sample) => sample.id}
                      getOptionLabel={(sample) =>
                        `${sample.sample_id} ${sample.alias ? `(${sample.alias})` : ""}`
                      }
                    />
                  </div>
                  <div className="col-6">
                    <ReactSelectionElement
                      name={"shared_printer"}
                      list={multiSelectPrinters}
                      label={intl.formatMessage({
                        id: "samplemanagement.printer.location",
                        defaultMessage: "Printer Location",
                      })}
                      onValueChange={(_, val) => setSharedPrinter(val?.value || "")}
                      isMulti={false}
                      isRequired={true}
                    />
                  </div>
                </div>
              )}
              {selectedSamples.length === 0 ? (
                <FormattedMessage
                  id={"samplemanagement.print.job.no-sample-selected"}
                  description={"No samples selected for printing"}
                />
              ) : (
                <table className={"table table-bordered table-sm tbl_lanzatable"}>
                  <thead>
                    <tr>
                      {headers
                        .filter((h) => !h?.hide || !isMultipleSamples)
                        .map((header) => (
                          <th className={"text-center"} key={header["id"]} style={{ minWidth: "60px" }}>
                            <FormattedMessage
                              id={header["id"]}
                              description={`${header["desc"]} Column Name`}
                            />
                            {header["isRequired"] && <Required />}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>{selectedSamples.map(renderSampleRow)}</tbody>
                </table>
              )}
            </>
          ) : (
            <FormattedMessage id={"samplemanagement.no.available.printers"} />
          ))}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-primary" onClick={onHide}>
          <FormattedMessage id={"general.closeButton"} description={"Close Modal Button"} />
        </Button>
        <Button
          variant="primary"
          onClick={onSubmitPrintLabels}
          disabled={statusPrint === API_STATUS.LOADING || !formValid}
        >
          <FormattedMessage
            id={statusPrint === API_STATUS.LOADING ? "samplemanagement.printing" : "samplemanagement.print"}
            description={"Print Button"}
          />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

ModalPrintLabels.propTypes = {
  sample: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]).isRequired,
  show: PropTypes.bool.isRequired,
  printers: PropTypes.object.isRequired,
  setPrinters: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}
